import React from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ChevronRight } from "lucide-react";

const HeroSection = () => {
  const { scrollYProgress } = useScroll();
  const yScroll = useTransform(scrollYProgress, [0, 1], [0, -200]);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const smoothMouseX = useSpring(mouseX, { stiffness: 60, damping: 20 });
  const smoothMouseY = useSpring(mouseY, { stiffness: 60, damping: 20 });

  const rotateX = useTransform(smoothMouseY, [-30, 30], [10, -10]);
  const rotateY = useTransform(smoothMouseX, [-30, 30], [-10, 10]);

  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    const x = (clientX / innerWidth - 0.5) * 60;
    const y = (clientY / innerHeight - 0.5) * 60;
    mouseX.set(x);
    mouseY.set(y);
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <section
      onMouseMove={handleMouseMove}
      className="relative w-full min-h-[700px] md:h-[700px] lg:h-[700px] lg:pt-[100px] pt-[60px] bg-[#020B1C] overflow-hidden font-sans perspective-1000 flex flex-col justify-center"
    >
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 z-10"
          style={{
            background: "linear-gradient(86.84deg, #000000 20%, #01386A 79%)"
          }}
        />
      </div>

      <div className="relative z-30 max-w-[1220px] w-full mx-auto px-[20px] lg:px-[80px] h-full flex flex-col md:flex-row-reverse items-center justify-between lg:gap-y-8 pt-[40px] md:pt-0">

        <div className="w-full md:hidden pointer-events-auto">
          <motion.h1
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="font-sora text-[28px] lg:relative absolute top-[20px] lg:top-0 sm:text-[45px] font-[500] leading-[1.2] tracking-tight text-white"
          >
            AI Scale-Up <br /> For Enterprises
          </motion.h1>
        </div>

        <div className="w-full md:w-1/2 flex items-center lg:ml-0 ml-[36px] justify-center" style={{ perspective: "1200px" }}>
          <motion.div
            style={{
              y: yScroll,
              x: smoothMouseX,
              rotateX: rotateX,
              rotateY: rotateY,
            }}
            className="relative w-full scale-125 sm:scale-100 sm:w-[350px] md:w-[450px] lg:w-[500px] flex items-center justify-center z-20 pointer-events-none "
          >
            <motion.img
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              src="/assets/what/hero-img.png"
              alt="Hero Graphic"
              className="w-full h-auto scale-150 object-contain drop-shadow-[0_20px_50px_rgba(0,100,255,0.3)] relative z-10"
            />

            <div
              className="absolute inset-0 z-20 pointer-events-none overflow-hidden"
              style={{
                WebkitMaskImage: `url('/assets/what/hero-img.png')`,
                WebkitMaskSize: "contain",
                WebkitMaskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskImage: `url('/assets/what/hero-img.png')`,
                maskSize: "contain",
                maskRepeat: "no-repeat",
                maskPosition: "center",
              }}
            >
              <motion.div
                className="absolute top-0 bottom-0 w-[40%] bg-gradient-to-r from-transparent via-[rgba(255,255,255,0.7)] to-transparent"
                style={{ skewX: "-25deg" }}
                animate={{
                  left: ["-100%", "200%"]
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  repeatDelay: 0.5
                }}
              />
            </div>
          </motion.div>
        </div>

        <div className="w-full md:w-1/2 flex flex-col justify-center pointer-events-none mt-[0px] sm:mt-0">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              visible: { transition: { staggerChildren: 0.2 } }
            }}
            className="max-w-[700px] pointer-events-auto text-left flex flex-col"
          >
            <motion.h1
              variants={fadeUp}
              className="hidden md:block font-graphik text-[28px] sm:text-[45px] lg:text-[56px] font-[500] leading-[1.1] mb-6 tracking-tight text-white"
            >
              AI Scale-Up <br /> For Enterprises
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-white max-w-full md:max-w-[550px] w-full text-[16px] lg:text-[18px] leading-[1.6] mb-8 lg:mb-10 font-[400] opacity-80 lg:pt-0 pt-[20px]"
            >
              From building products end-to-end and upskilling your team to cloud solutions and ongoing support, we’re here to help your business grow smoothly and confidently.
            </motion.p>

            <motion.a
              href="/what-we-do"
              variants={fadeUp}
              className="group inline-flex items-center gap-3 text-white border-b border-white/30 pb-1 font-[500] text-[16px] lg:text-[18px] hover:text-[#ED5800] hover:border-[#ED5800] transition-all duration-300 w-fit"
            >
              See What We Do
              <ChevronRight
                size={22}
                className="transition-transform duration-300 group-hover:translate-x-2 text-[#ED5800]"
              />
            </motion.a>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default HeroSection;